import './sale-countdown.css';

type RandomState = {
  endTime: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseRandomState(raw: unknown): RandomState | null {
  if (!isRecord(raw)) return null;
  const endTime = raw['endTime'];
  if (typeof endTime !== 'number' || !Number.isFinite(endTime) || endTime <= 0) {
    return null;
  }
  return { endTime };
}

type CountdownElements = {
  days: HTMLElement;
  hours: HTMLElement;
  minutes: HTMLElement;
  seconds: HTMLElement;
};

type SaleConfig = {
  type: 'sale';
  endTime: number;
  endedText: string;
};

type RandomConfig = {
  type: 'random';
  minDays: number;
  maxDays: number;
  storageKey: string;
};

type TimeUnits = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_MINUTE = 60_000;
const MS_PER_SECOND = 1_000;

function padZero(num: number): string {
  return num < 10 ? `0${num}` : String(num);
}

function randomEndTime(minDays: number, maxDays: number): number {
  const minMs = minDays * MS_PER_DAY;
  const maxMs = maxDays * MS_PER_DAY;
  const offsetMs = Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
  return Date.now() + offsetMs;
}

function storageGet(key: string): RandomState | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return parseRandomState(parsed);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: RandomState): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode, quota, etc.) — silently ignore.
  }
}

function calculateTimeUnits(distance: number): TimeUnits {
  return {
    days: Math.floor(distance / MS_PER_DAY),
    hours: Math.floor((distance % MS_PER_DAY) / MS_PER_HOUR),
    minutes: Math.floor((distance % MS_PER_HOUR) / MS_PER_MINUTE),
    seconds: Math.floor((distance % MS_PER_MINUTE) / MS_PER_SECOND),
  };
}

function updateDisplay(elements: CountdownElements, time: TimeUnits): void {
  elements.days.textContent = padZero(time.days);
  elements.hours.textContent = padZero(time.hours);
  elements.minutes.textContent = padZero(time.minutes);
  elements.seconds.textContent = padZero(time.seconds);
}

function showEndedMessage(container: HTMLElement, message: string): void {
  const ul = container.querySelector('ul');
  if (!ul) return;

  const items = ul.querySelectorAll<HTMLElement>('li:not(.countdown-ended)');
  items.forEach((item) => {
    item.style.display = 'none';
  });

  const existing = ul.querySelector<HTMLElement>('.countdown-ended');
  if (existing) {
    existing.style.display = '';
    existing.textContent = message;
    return;
  }

  const li = document.createElement('li');
  li.className = 'countdown-ended fs-3';
  li.textContent = message;
  ul.appendChild(li);
}

function getElements(container: HTMLElement): CountdownElements | null {
  const days = container.querySelector<HTMLElement>('.countdown-days');
  const hours = container.querySelector<HTMLElement>('.countdown-hours');
  const minutes = container.querySelector<HTMLElement>('.countdown-minutes');
  const seconds = container.querySelector<HTMLElement>('.countdown-seconds');
  if (!days || !hours || !minutes || !seconds) return null;
  return { days, hours, minutes, seconds };
}

function initSaleCountdown(
  container: HTMLElement,
  elements: CountdownElements,
  config: SaleConfig,
): void {
  const tick = (): boolean => {
    const distance = config.endTime - Date.now();
    if (distance <= 0) {
      showEndedMessage(container, config.endedText);
      return false;
    }
    updateDisplay(elements, calculateTimeUnits(distance));
    return true;
  };

  tick();
  container.classList.add('ready');

  const intervalId = window.setInterval(() => {
    if (!tick()) {
      window.clearInterval(intervalId);
    }
  }, 1000);
}

function initRandomCountdown(
  container: HTMLElement,
  elements: CountdownElements,
  config: RandomConfig,
): void {
  function getOrCreateState(): RandomState {
    const stored = storageGet(config.storageKey);
    const now = Date.now();

    if (stored && stored.endTime > now) {
      return stored;
    }

    const fresh: RandomState = { endTime: randomEndTime(config.minDays, config.maxDays) };
    storageSet(config.storageKey, fresh);
    return fresh;
  }

  const tick = (): void => {
    const state = getOrCreateState();
    const distance = state.endTime - Date.now();
    updateDisplay(elements, calculateTimeUnits(distance));
  };

  tick();
  container.classList.add('ready');
  window.setInterval(tick, 1000);
}

function parseSaleConfig(container: HTMLElement): SaleConfig | null {
  const endRaw = container.dataset['end'];
  const endedText = container.dataset['endedText'] ?? 'Sale Ended';
  if (!endRaw) return null;
  const endTime = Number.parseInt(endRaw, 10);
  if (!Number.isFinite(endTime) || endTime <= 0) return null;
  return { type: 'sale', endTime, endedText };
}

function parseRandomConfig(container: HTMLElement): RandomConfig {
  const minDaysRaw = container.dataset['minDays'];
  const maxDaysRaw = container.dataset['maxDays'];
  const storageKey = container.dataset['storageKey'] ?? 'saleCountdownRandom';

  const minDays = minDaysRaw ? Number.parseInt(minDaysRaw, 10) : 1;
  const maxDays = maxDaysRaw ? Number.parseInt(maxDaysRaw, 10) : 3;

  return {
    type: 'random',
    minDays: Number.isFinite(minDays) && minDays > 0 ? minDays : 1,
    maxDays: Number.isFinite(maxDays) && maxDays > 0 ? maxDays : 3,
    storageKey,
  };
}

function initCountdown(container: HTMLElement): void {
  const type = container.dataset['type'];
  const elements = getElements(container);
  if (!elements) return;

  if (type === 'sale') {
    const config = parseSaleConfig(container);
    if (!config) return;
    initSaleCountdown(container, elements, config);
    return;
  }

  if (type === 'random') {
    const config = parseRandomConfig(container);
    initRandomCountdown(container, elements, config);
  }
}

function init(): void {
  const timers = document.querySelectorAll<HTMLElement>('.countdown-timer');
  timers.forEach((timer) => {
    initCountdown(timer);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
